/**
 * HR Type Resolver — warstwa rozwiazywania typow wnioskow HR.
 *
 * Jedyny punkt importu dla komponentow UI.
 * Laczy konfiguracje z hrConfigService z komponentami React (ikony).
 *
 * Konwertuje stringowe nazwy ikon ("FiClock") na komponenty React (<FiClock />).
 *
 * UWAGA: Wszystkie metody pobierajace dane sa ASYNC (API backend).
 */

import {
    FiCalendar,
    FiClock,
    FiSun,
    FiActivity,
    FiHeart,
    FiStar,
    FiCoffee,
    FiMinus,
    FiWifi,
    FiHome,
    FiFileText,
    FiBookOpen,
    FiDollarSign,
    FiUser,
    FiCreditCard,
    FiUsers,
    FiFile,
    FiShield,
    FiCheckSquare,
    FiBriefcase,
    FiRotateCcw,
    FiHelpCircle,
    FiFolder,
    FiEdit,
    FiTool,
    FiClipboard,
    FiAward,
    FiTruck,
    FiPackage,
    FiSend,
    FiUserPlus,
    FiUserMinus,
    FiAlertTriangle,
    FiTrendingUp,
    FiMapPin,
    FiKey,
    FiLock,
    FiSmile,
    FiSearch,
} from "react-icons/fi";

import hrConfigService from "./hrConfigService";

// --- Icon Map: string -> React component ---
const ICON_MAP = {
    FiCalendar,
    FiClock,
    FiSun,
    FiActivity,
    FiHeart,
    FiStar,
    FiCoffee,
    FiMinus,
    FiWifi,
    FiHome,
    FiFileText,
    FiBookOpen,
    FiDollarSign,
    FiUser,
    FiCreditCard,
    FiUsers,
    FiFile,
    FiShield,
    FiCheckSquare,
    FiBriefcase,
    FiRotateCcw,
    FiHelpCircle,
    FiFolder,
    FiEdit,
    FiTool,
    FiClipboard,
    FiAward,
    FiTruck,
    FiPackage,
    FiSend,
    FiUserPlus,
    FiUserMinus,
    FiAlertTriangle,
    FiTrendingUp,
    FiMapPin,
    FiKey,
    FiLock,
    FiSmile,
    FiSearch,
};

const DEFAULT_ICON = FiHelpCircle;

/**
 * Resolve icon string to React component.
 */
const resolveIcon = (iconName) => {
    if (!iconName) return DEFAULT_ICON;
    if (typeof iconName !== "string") return iconName; // Already a component
    return ICON_MAP[iconName] || DEFAULT_ICON;
};

/**
 * Enrich a raw config type with resolved React icon.
 */
const enrichType = (rawType) => {
    if (!rawType) return null;

    const approvalFlags = hrConfigService.getApprovalFlags(rawType.approvalFlow);

    return {
        ...rawType,
        icon: resolveIcon(rawType.icon),
        iconName: rawType.icon, // Keep original string
        // Backward compatibility — alias category = categoryId
        category: rawType.categoryId || rawType.category,
        // Backward compatibility with hrDataService
        requiresManager: approvalFlags.requiresManager,
        requiresHr: approvalFlags.requiresHr,
        requiresDirector: approvalFlags.requiresDirector,
    };
};

/**
 * Enrich a raw config category with resolved React icon.
 */
const enrichCategory = (rawCat) => {
    if (!rawCat) return null;
    return {
        ...rawCat,
        icon: resolveIcon(rawCat.icon),
        iconName: rawCat.icon,
    };
};

// =====================================================================
//                      PUBLIC API
// =====================================================================

const hrTypeResolver = {

    /**
     * Inicjalizacja -- no-op (backend handles data).
     */
    async init() {
        // No-op -- backend handles initialisation
    },

    // -- CATEGORIES --

    /**
     * Zwraca wszystkie aktywne kategorie (z komponentami ikon).
     */
    async getCategories() {
        const raw = await hrConfigService.getCategories();
        return (raw || []).map(enrichCategory);
    },

    /**
     * Zwraca pojedyncza kategorie.
     */
    async getCategory(id) {
        const raw = await hrConfigService.getCategory(id);
        return enrichCategory(raw);
    },

    // -- REQUEST TYPES --

    /**
     * Zwraca wszystkie aktywne typy wnioskow (z komponentami ikon).
     */
    async getAllTypes() {
        const raw = await hrConfigService.getRequestTypes();
        return (raw || []).map(enrichType);
    },

    /**
     * Zwraca pojedynczy typ wniosku.
     */
    async getRequestType(typeId) {
        const raw = await hrConfigService.getRequestType(typeId);
        return enrichType(raw);
    },

    /**
     * Zwraca typy dla danej kategorii.
     */
    async getTypesByCategory(categoryId) {
        const raw = await hrConfigService.getTypesByCategory(categoryId);
        return (raw || []).map(enrichType);
    },

    /**
     * [brief wlasciciela 2026-08-30, pkt 6] Kategorie z typami, KTORE PRACOWNIKOWI
     * PRZYSLUGUJA — wzbogacone o komponenty ikon. Zwraca `null`, gdy API zawiodlo
     * (ekran ma odroznic „nic Ci nie przysluguje" od „nie udalo sie pobrac").
     *
     * Zastapilo cztery funkcje liczace dostepnosc W PRZEGLADARCE (getAvailableTypes,
     * getAvailableTypesGrouped, getAllTypesWithAvailability, getAllTypesGroupedWithAvailability)
     * — patrz komentarz w hrConfigService.
     */
    async getAvailableTypesForUser(forUserId = null) {
        const groups = await hrConfigService.getAvailableTypesForUser(forUserId);
        if (groups === null) return null;
        return groups.map((group) => ({
            ...enrichCategory(group),
            types: (group.types || []).map(enrichType),
        }));
    },

    // -- MANAGER-ONLY TYPES --

    /**
     * Zwraca aktywne typy wnioskow dla przelozonych (enriched).
     */
    async getManagerOnlyTypes() {
        const raw = await hrConfigService.getManagerOnlyTypes();
        return (raw || []).map(enrichType);
    },

    // -- BALANCE POOLS --

    async getBalancePools() {
        return hrConfigService.getBalancePools();
    },

    async getBalancePool(id) {
        return hrConfigService.getBalancePool(id);
    },

    // -- TEMPLATES --

    async getTemplate(typeId) {
        return hrConfigService.getTemplate(typeId);
    },

    // USUNIETE (audyt 2026-08-29, L-11 + brief wlasciciela): renderTemplate() i getTemplateVariables().
    // renderTemplate byl DRUGIM, KLIENCKIM rendererem dokumentu: podstawial tokeny {x} w wycofanym
    // formacie v1, czytal wzor z ZYWEGO typu (a nie ze snapshotu wniosku) i pokazywal cos innego niz
    // realny PDF. Podglad dokumentu ma jedno zrodlo — serwerowe /hr-requests/{id}/preview-html,
    // czyli ten sam renderer, ktory tworzy plik PDF.

    // -- APPROVAL FLOW --

    /**
     * Zwraca flagi zatwierdzania dla danego typu.
     */
    async getApprovalFlags(typeId) {
        const type = await hrConfigService.getRequestType(typeId);
        if (!type) return { requiresManager: true, requiresHr: true };
        return hrConfigService.getApprovalFlags(type.approvalFlow);
    },

    /**
     * Opis flow zatwierdzania (do wyswietlenia w UI).
     * SYNCHRONICZNA -- nie wymaga API.
     */
    getApprovalFlowLabel(approvalFlow) {
        switch (approvalFlow) {
            case "manager": return "Przełożony";
            case "manager_hr": return "Przełożony → HR";
            case "director_hr": return "Dyrektor działu → HR";
            case "manager_director_hr": return "Przełożony → Dyrektor → HR";
            case "hr_only": return "HR";
            case "auto": return "Automatyczna realizacja";
            case "president": return "Prezes (dla dyrektorów)";
            case "president_hr": return "Prezes → HR (dla dyrektorów)";
            default: return "Przełożony → HR";
        }
    },

    // -- ICON UTILITIES --

    /**
     * Resolve icon name to component.
     * SYNCHRONICZNA -- nie wymaga API.
     */
    resolveIcon,

    /**
     * Zwraca liste dostepnych ikon (do selectora w admin UI).
     * SYNCHRONICZNA -- nie wymaga API.
     */
    getAvailableIcons() {
        return Object.entries(ICON_MAP).map(([name, component]) => ({
            name,
            component,
        }));
    },

    // -- CATEGORY MIGRATION (backward compat) --

    /**
     * Mapuje stara kategorie na nowa.
     * SYNCHRONICZNA -- nie wymaga API.
     */
    migrateCategory(oldCategoryId) {
        return hrConfigService.CATEGORY_MIGRATION_MAP[oldCategoryId] || oldCategoryId;
    },
};

export default hrTypeResolver;
